import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsDark } from '../../lib/useIsDark.js';

export type ShotName =
  | 'layout-reels'
  | 'layout-list'
  | 'layout-grid'
  | 'layout-topics'
  | 'detail'
  | 'hero-1'
  | 'hero-2'
  | 'hero-3';

/** Screenshots exist in Czech and English only; every other UI language falls back to the English set. */
export function shotLang(language: string): 'cs' | 'en' {
  return language.startsWith('cs') ? 'cs' : 'en';
}

/** Ordered candidates for one shot: the UI-language file first, the Czech one as a fallback
 * (so a landing without regenerated English assets still shows a screenshot). */
export function shotSources(name: ShotName, dark: boolean, lang: 'cs' | 'en'): string[] {
  const base = `/landing/${name}-${dark ? 'dark' : 'light'}`;
  return lang === 'en' ? [`${base}-en.webp`, `${base}.webp`] : [`${base}.webp`];
}

/** Real app screenshot generated by `pnpm landing:shots` (e2e/landing-shots.spec.ts) into
 * public/landing/<name>-<light|dark>[-en].webp. Falls back to a neutral panel if every candidate is missing. */
export default function Shot({
  name,
  alt,
  width,
  height,
  className = '',
  eager = false,
  priority = eager,
}: {
  name: ShotName;
  alt: string;
  width: number;
  height: number;
  className?: string;
  eager?: boolean;
  /** `fetchpriority=high` — only the very first hero frame should ask for it */
  priority?: boolean;
}) {
  const dark = useIsDark();
  const { i18n } = useTranslation();
  const sources = shotSources(name, dark, shotLang(i18n.language));
  const preferred = sources[0];
  const [attempt, setAttempt] = useState(0);
  // theme or language switched: start over with the preferred candidate
  useEffect(() => setAttempt(0), [preferred]);
  const src = sources[attempt];
  if (!src) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={`bg-neutral-200 dark:bg-slate-700 ${className}`}
        style={{ aspectRatio: `${width} / ${height}` }}
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onError={() => setAttempt((n) => n + 1)}
      className={`block h-full w-full object-cover object-top ${className}`}
    />
  );
}
